import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import {
  CheckIcon,
  CircleCheckIcon,
  CircleNullIcon,
  CircleXIcon,
} from '@persona-id/design-tokens/icons';
import { upperFirst } from 'lodash/fp';
import React from 'react';
import styled from 'styled-components';

import { ChecksTableTooltip } from 'dashboard/components/ChecksTable/ChecksTableRow';
import { ChecksTableRowMetadataProps } from 'dashboard/components/ChecksTable/ChecksTableRowMetadata';
import { computeAamvaAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/aamvaAttributeChecks';
import { computeBusinessWebsiteAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/businessWebsiteAttributeChecks';
import { computeBusinessWebsiteDomainAgeDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/businessWebsiteDomainAgeDetection';
import { computeBusinessWebsiteDomainExpirationDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/businessWebsiteDomainExpirationDetection';
import { computeBusinessWebsiteDomainResolvableDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/businessWebsiteDomainResolvableDetection';
import { computeDatabaseAadhaarAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseAadhaarAttributeChecks';
import { computeDatabaseAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseAttributeChecks';
import { computeDatabaseAttributeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseAttributeComparison';
import { computeDatabaseBusinessAssociatedPersonsComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessAssociatedPersonsComparison';
import { computeDatabaseBusinessAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessAttributeChecks';
import { computeDatabaseBusinessFootprintAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessFootprintAttributeChecks';
import { computeDatabaseBusinessValidRegistrationNumberFormatMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessValidRegistrationNumberFormat';
import { computeDatabaseInquiryComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseInquiryComparison';
import { computeDatabasePhoneCarrierAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databasePhoneCarrierAttributeChecks';
import { computeDatabaseStandardAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseStandardAttributeChecks';
import { computeDatabaseTwoPlusTwoComparisonDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseTwoPlusTwoComparisonDetection';
import { computeDatabaseVatNumberAttributeChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseVatNumberAttributeChecks';
import { computeDocumentExpiredDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentExpiredDetection';
import { computeDocumentExtractedPropertiesDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentExtractedPropertiesDetection';
import { computeDocumentPdfAbnormalFontDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentPdfAbnormalFontDetection';
import { computeDocumentPdfAnnotationDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentPdfAnnotationDetection';
import { computeDocumentPdfContentTypeDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentPdfContentTypeDetection';
import { computeDocumentPdfEditorDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentPdfEditorDetection';
import { computeDocumentPdfInconsistentTimestampDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentPdfInconsistentTimestampDetection';
import { computeDocumentRecencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentRecencyDetection';
import { computeDocumentSyntheticContentDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/documentSyntheticContentDetection';
import { computeIdAamvaDatabaseLookupMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idAamvaDatabaseLookup';
import { computeIdAccountComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idAccountComparison';
import { computeIdAgeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idAgeComparison';
import { computeIdAgeInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idAgeInconsistencyDetection';
import { computeIdAttributeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idAttributeComparison';
import { computeIdBarcodeDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idBarcodeDetection';
import { computeIdBarcodeInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idBarcodeInconsistencyDetection';
import { computeIdBlurDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idBlurDetection';
import { computeIdColorInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idColorInconsistencyDetection';
import { computeIdCompromisedDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idCompromisedDetection';
import { computeIdDisallowedCountryDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idDisallowedCountryDetection';
import { computeIdDisallowedTypeDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idDisallowedTypeDetection';
import { computeIdDoubleSideDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idDoubleSideDetection';
import { computeIdExperimentalModelDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idExperimentalModelDetection';
import { computeIdExpiredDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idExpiredDetection';
import { computeIdExtractedPropertiesDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idExtractedPropertiesDetection';
import { computeIdExtractionInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idExtractionInconsistencyDetection';
import { computeIdFabricationDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idFabricationDetection';
import { computeIdInconsistentRepeatDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idInconsistentRepeatDetection';
import { computeIdInquiryComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idInquiryComparison';
import { computeIdMrzDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idMrzDetection';
import { computeIdMrzInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idMrzInconsistencyDetection';
import { computeIdNfcAttributeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idNfcAttributeComparison';
import { computeIdNumberFormatInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idNumberFormatInconsistencyDetection';
import { computeIdPaperDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idPaperDetection';
import { computeIdPortraitClarityDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idPortraitClarityDetection';
import { computeIdPublicFigureDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idPublicFigureDetection';
import { computeIdRepeatDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idRepeatDetection';
import { computeIdSelfieComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idSelfieComparison';
import { computeIdTamperDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idTamperDetection';
import { computeIdValidDatesDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/idValidDatesDetection';
import { computeMobileDriversLicenseAgeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/mobileDriversLicenseAgeComparison';
import { computeSelfieAccountComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieAccountComparison';
import { computeSelfieAgeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieAgeComparison';
import { computeSelfieAgeInconsistencyDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieAgeInconsistencyDetection';
import { computeSelfieAgeThresholdComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieAgeThresholdComparison';
import { computeSelfieAttributeComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieAttributeComparison';
import { computeSelfieExperimentalModelDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieExperimentalModelDetection';
import { computeSelfieIdComparisonMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieIdComparison';
import { computeSelfieLivenessDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieLivenessDetection';
import { computeSelfiePublicFigureDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfiePublicFigureDetection';
import { computeSelfieRepeatDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieRepeatDetection';
import { computeSelfieSimilarBackgroundDetectionMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata/selfieSimilarBackgroundDetection';
import RiskLevelSticker from 'dashboard/components/RiskLevelSticker';
import { TextWithDashedUnderline } from 'dashboard/components/Typography';
import { getStatusIcon } from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseVerification/ComparisonChecksTable';
import HelpIcon from 'dashboard/components-lib/HelpIcon/HelpIcon';
import {
  Check,
  CheckAttributeMatch,
  CheckName,
  CheckStatus,
  ComparisonResult as ComparisonResultIdentityComparison,
  DatabaseAttributeComparison,
  DatabaseBusinessAssociatedPersonsComparison,
  DatabaseTwoPlusTwoDetection,
  DocumentExtractedPropertiesDetection,
  DocumentSyntheticContentDetection,
  GroupComparisonResult,
  IdAamvaDatabaseLookup,
  IdAccountComparison,
  IdAgeInconsistencyDetection,
  IdAttributeComparison,
  IdBarcodeInconsistencyDetection,
  IdColorInconsistencyDetection,
  IdCompromisedDetection,
  IdExperimentalModelDetection,
  IdExpiredDetection,
  IdExtractedPropertiesDetection,
  IdExtractionInconsistencyDetection,
  IdFabricationDetection,
  IdInconsistentRepeatDetection,
  IdInquiryComparison,
  IdNfcAttributeComparison,
  IdPaperDetection,
  IdPortraitClarityDetection,
  IdRepeatDetection,
  IdSelfieComparison,
  IdTamperDetection,
  IdValidDatesDetection,
  Match,
  MobileDriversLicenseAgeComparison,
  SelfieAccountComparison,
  SelfieAgeComparison,
  SelfieAgeInconsistencyDetection,
  SelfieAgeThresholdComparison,
  SelfieAttributeComparison,
  SelfieExperimentalModelDetection,
  SelfieIdComparison,
  SelfieRepeatDetection,
  SelfieSimilarBackgroundDetection,
} from 'dashboard/models/check';
import {
  AttributeComparison,
  AttributeComparisonResultStatus,
  ComparisonDescriptionMap,
  ComparisonNameMap,
} from 'dashboard/models/check-result/check-requirement';
import { ComparisonResult } from 'dashboard/models/check-result/check-result';
import {
  MatchGroupResult,
  Verification,
  VerificationAamva,
  VerificationBusinessWebsite,
  VerificationDatabase,
  VerificationDatabaseAadhaar,
  VerificationDatabaseBusiness,
  VerificationDatabaseBusinessFootprint,
  VerificationDatabasePhoneCarrier,
  VerificationDatabaseStandard,
  VerificationDatabaseVatNumber,
} from 'dashboard/models/verification';
import { RiskData, RiskLevel, RiskLevelValue } from 'dashboard/models/verification-check';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import Tooltip from 'design-system/components/Tooltip';
import { ApiModel } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

export enum CheckMetadataItemType {
  Simple = 'simple',
  Group = 'group',
  Table = 'table',
  DynamicTable = 'dynamic-table',
}

interface SimpleCheckMetadataItem {
  type: CheckMetadataItemType.Simple;
  label: string;
  value: React.ReactNode;
  labelHelpText?: string; // Optional help text to display on hover over (?) icon next to the label
}

export interface GroupedCheckMetadataItem {
  type: CheckMetadataItemType.Group;
  heading: {
    title: string;
    sticker?: React.ReactNode;
  };
  items: Array<{
    label: string;
    value: React.ReactNode;
    labelHelpText?: string;
  }>;
}

export type TableRow = (React.ReactNode | string)[];

export interface TableCheckMetadataItem {
  type: CheckMetadataItemType.Table;
  heading: {
    title: string;
    sticker?: React.ReactNode;
  };
  subheading?: React.ReactNode | string;
  rowHeaders: React.ReactNode[];
  rows: TableRow[];
  tableEmptyState?: string;
}

interface DynamicTableOption {
  value: string;
  label: string | React.ReactNode;
}

// This interface is used for tables where the data changes based on the selected option
// (e.g. DBV Match Group results). Each `value` in DynamicTableOption corresponds to a
// key in the `rows` object.
export interface DynamicTableCheckMetadataItem {
  type: CheckMetadataItemType.DynamicTable;
  heading: {
    title: string;
    sticker?: React.ReactNode;
  };
  options: DynamicTableOption[];
  rowHeaders: React.ReactNode[];
  rows: { [key: string]: TableRow[] };
  tableEmptyState?: string;
}

export type CheckMetadataItem =
  | SimpleCheckMetadataItem
  | GroupedCheckMetadataItem
  | TableCheckMetadataItem
  | DynamicTableCheckMetadataItem;

export type CheckMetadata = Omit<ChecksTableRowMetadataProps, 'check' | 'id'>;

function createSimpleCheckMetadataItem(
  label: string,
  value: React.ReactNode,
  labelHelpText?: string,
): SimpleCheckMetadataItem {
  return {
    type: CheckMetadataItemType.Simple,
    label,
    value,
    ...(labelHelpText && { labelHelpText }),
  };
}

export function createSimpleCheckMetadataArray(
  items: Array<{ label: string; value: React.ReactNode; labelHelpText?: string }>,
): SimpleCheckMetadataItem[] {
  return items.map((item) =>
    createSimpleCheckMetadataItem(item.label, item.value, item.labelHelpText),
  );
}

export function createRiskLevelMetadataItem(
  label: string,
  risks: RiskData[] | undefined,
  reason: string,
): SimpleCheckMetadataItem | null {
  if (!risks) {
    return null;
  }
  const riskData = [...risks]
    .sort((a: RiskData, b: RiskData) => {
      const levelA = RiskLevelValue[a.level as RiskLevel] ?? -1; // Default to -1 for undefined levels
      const levelB = RiskLevelValue[b.level as RiskLevel] ?? -1; // Default to -1 for undefined levels
      return levelB - levelA; // Descending order
    })
    .find((r: RiskData) => (r.reason as string) === reason);
  if (riskData)
    return createSimpleCheckMetadataItem(
      label,
      RiskLevelSticker({ riskLevel: riskData['level'] as RiskLevel }),
    );
  return null;
}

export function createAttributeComparisonTableCheckMetadatas(
  comparisons: AttributeComparison[],
  comparisonCheckType: ComparisonCheckType,
): TableCheckMetadataItem[] {
  let submittedLabel = 'Extracted';
  let prefilledLabel = 'Claimed';
  if (comparisonCheckType === ComparisonCheckType.DatabaseInquiryComparison) {
    submittedLabel = 'Submitted';
    prefilledLabel = 'Prefilled';
  }

  const rows: TableRow[] = comparisons.map((comparison) => {
    const attributeName = comparison['attribute-description'] || 'Attribute';
    const attributeValue = comparison['attribute-values']?.[0]; // Assuming each comparison returns just one row
    const submittedValue = attributeValue?.['normalized-extracted-attribute'] || '';
    const prefilledValue = attributeValue?.['normalized-claimed-attribute'] || '';
    const comparisonResult = (
      <>
        {comparison['attribute-comparison-results']?.map((comparisonResult, index) => (
          <AttributeComparisonResult key={`attribute-comparison-result-${index}`}>
            {comparisonResult['comparison-type'] &&
              ComparisonNameMap[comparisonResult['comparison-type']]}{' '}
            {comparisonResult.status === 'passed' ? (
              <Sticker variant='success'>Passed</Sticker>
            ) : (
              <Sticker variant='danger'>Failed</Sticker>
            )}
          </AttributeComparisonResult>
        ))}
      </>
    );

    // Log a Sentry so we know when customers are trying to view an old Verification
    // that does not have a `comparison['attribute-description']`
    if (!comparison['attribute-description']) {
      captureMessage(
        `The comparison does not have an attribute-description, so falling back to the label "Attribute" for the heading. Attribute keys: ${comparison[
          'attribute-keys'
        ]?.join(', ')}`,
        'warning',
      );
    }

    return [attributeName, submittedValue, prefilledValue, comparisonResult];
  });

  return [
    {
      type: CheckMetadataItemType.Table,
      heading: {
        title: 'Comparison Results',
      },
      rowHeaders: ['Attribute', submittedLabel, prefilledLabel, 'Comparison Results'],
      rows,
      tableEmptyState: rows.length === 0 ? 'No data available' : undefined,
    },
  ];
}

const AttributeComparisonResult = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};

  &:not(:first-child) {
    margin-top: ${Spacing[2]};
  }
`;

export enum ComparisonCheckType {
  DatabaseAttributeComparison = 'DatabaseAttributeComparison',
  DatabaseInquiryComparison = 'DatabaseInquiryComparison',
  IdAccountComparison = 'IdAccountComparison',
  IdAttributeComparison = 'IdAttributeComparison',
  IdExtractionInconsistencyDetection = 'IdExtractionInconsistencyDetection',
  IdInquiryComparison = 'IdInquiryComparison',
  IdNfcAttributeComparison = 'IdNfcAttributeComparison',
}

// We use this instead of just checking `value || '-'` because we want to render 0 as 0 (which is falsy)
export function formatMetadataValue(value: any): React.ReactNode {
  if (value === 0) {
    return 0;
  }

  return value || '-';
}

export function renderCheckNameCell(checkName: string, description?: string) {
  if (description) {
    return (
      <Tooltip
        side='right'
        trigger={<TextWithDashedUnderline>{checkName}</TextWithDashedUnderline>}
      >
        <ChecksTableTooltip>{description}</ChecksTableTooltip>
      </Tooltip>
    );
  }

  return <>{checkName}</>;
}

export function renderMatchGroupResultsTooltip(
  matchGroupResults: MatchGroupResult[],
  customTitle?: string,
) {
  return (
    <HelpIcon placement='right'>
      <MatchGroupsResultsContainer>
        <b>{customTitle || 'Applicable Match Groups'}</b>
        <Spacer height={1} />
        {matchGroupResults.map((matchGroupResult, index) => {
          let statusIcon;

          switch (matchGroupResult.status) {
            case CheckStatus.Passed:
              statusIcon = <CircleCheckIcon size={14} color={Color.CoreGreen700} />;
              break;
            case CheckStatus.Failed:
              statusIcon = <CircleXIcon size={14} color={Color.CoreRed700} />;
              break;
            case CheckStatus.NotApplicable:
              statusIcon = <CircleNullIcon size={14} color={Color.CoreSlate700} />;
              break;
          }

          return (
            <MatchGroupsResultsRow key={`match-group-${index}`}>
              <div>{statusIcon}</div>
              <div>{matchGroupResult.description}</div>
            </MatchGroupsResultsRow>
          );
        })}
      </MatchGroupsResultsContainer>
    </HelpIcon>
  );
}

const MatchGroupsResultsContainer = styled.div`
  ${Typography.TextMedium};
  color: ${Color.TextPrimary};
`;

const MatchGroupsResultsRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;
  margin-bottom: ${Spacing[1]};

  svg {
    vertical-align: middle;
  }
`;

export function renderMatchResultRowHeader(comparisonMethods: string[]) {
  return (
    <Tooltip side='right' trigger={<TextWithDashedUnderline>Match Result</TextWithDashedUnderline>}>
      <ChecksTableTooltip>
        {comparisonMethods.map((comparisonMethod) => (
          <React.Fragment key={comparisonMethod}>
            <div>
              <b>{ComparisonNameMap[comparisonMethod]}</b>
            </div>
            <div>{ComparisonDescriptionMap[comparisonMethod]}</div>
            <Spacer height={1} />
          </React.Fragment>
        ))}
      </ChecksTableTooltip>
    </Tooltip>
  );
}

// Renders a match result cell with an optional tooltip.
// Depending on the parameters passed, either `comparisonResults` or `minimumMatch` will determine the
// content of the tooltip (`minimumMatch` is only used in legacy cases).
export function renderMatchResultCell(
  matchResult: CheckAttributeMatch | Match,
  comparisonResults?:
    | ComparisonResult[]
    | ComparisonResultIdentityComparison[]
    | GroupComparisonResult[],
  minimumMatch?: string,
) {
  if (!matchResult) {
    return <></>;
  }

  if (comparisonResults) {
    return (
      <Tooltip
        side='right'
        trigger={<TextWithDashedUnderline>{upperFirst(matchResult)}</TextWithDashedUnderline>}
      >
        <ChecksTableTooltip>
          <ComparisonResultsList>
            {comparisonResults.map((comparisonResult) => {
              const resultType =
                'comparison-type' in comparisonResult
                  ? comparisonResult['comparison-type']
                  : comparisonResult.type;

              return (
                <li key={resultType}>
                  {getStatusIcon(comparisonResult.status)}
                  <span>{ComparisonNameMap[resultType]}</span>
                  <span>(min: {comparisonResult['match-level-minimum']})</span>
                </li>
              );
            })}
          </ComparisonResultsList>
        </ChecksTableTooltip>
      </Tooltip>
    );
  } else if (minimumMatch) {
    return (
      <Tooltip
        side='right'
        trigger={<TextWithDashedUnderline>{upperFirst(matchResult)}</TextWithDashedUnderline>}
      >
        <ChecksTableTooltip>Min: {minimumMatch}</ChecksTableTooltip>
      </Tooltip>
    );
  } else {
    return <>{upperFirst(matchResult)}</>;
  }
}

export function renderStatusCell(status: CheckStatus | AttributeComparisonResultStatus) {
  switch (status) {
    case 'passed':
      return <Sticker variant='success'>Passed</Sticker>;
    case 'failed':
      return <Sticker variant='danger'>Failed</Sticker>;
    case 'not_applicable':
      return <Sticker variant='basic'>N/A</Sticker>;
    default:
      return null;
  }
}

const ComparisonResultsList = styled.ul`
  li {
    display: flex;
    flex-direction: row;
    column-gap: ${Spacing[1]};
    align-items: center;
    margin: ${Spacing[1]};
  }
`;

export function renderRequiredCell(isRequired: boolean) {
  if (isRequired) {
    return <CheckIcon />;
  }

  return null;
}

export const computeCheckMetadata = (
  check: Check,
  hasPermissionVerificationGovIdNumberView: boolean,
  frontPhotoUrl?: string,
  backPhotoUrl?: string,
): CheckMetadata => {
  switch (check.name) {
    case CheckName.BusinessWebsiteDomainAgeDetection:
      return computeBusinessWebsiteDomainAgeDetectionMetadata(check);
    case CheckName.BusinessWebsiteDomainExpirationDetection:
      return computeBusinessWebsiteDomainExpirationDetectionMetadata(check);
    case CheckName.BusinessWebsiteDomainResolvableDetection:
      return computeBusinessWebsiteDomainResolvableDetectionMetadata(check);
    case CheckName.DatabaseAttributeComparison:
      return computeDatabaseAttributeComparisonMetadata(check as DatabaseAttributeComparison);
    case CheckName.DatabaseBusinessAssociatedPersonsComparison:
      return computeDatabaseBusinessAssociatedPersonsComparisonMetadata(
        check as DatabaseBusinessAssociatedPersonsComparison,
      );
    case CheckName.DatabaseBusinessValidRegistrationNumberFormat:
      return computeDatabaseBusinessValidRegistrationNumberFormatMetadata(check);
    case CheckName.DatabaseInquiryComparison:
      return computeDatabaseInquiryComparisonMetadata(check);
    case CheckName.DatabaseTwoPlusTwoDetection:
      return computeDatabaseTwoPlusTwoComparisonDetectionMetadata(
        check as DatabaseTwoPlusTwoDetection,
      );
    case CheckName.DocumentExpiredDetection:
      return computeDocumentExpiredDetectionMetadata(check);
    case CheckName.DocumentExtractedPropertiesDetection:
      return computeDocumentExtractedPropertiesDetectionMetadata(
        check as DocumentExtractedPropertiesDetection,
      );
    // Note: DocumentImageInconsistentTimestampDetection uses the exact same logic as DocumentPdfInconsistentTimestampDetection,
    // since it contains the same metadata attributes.
    case CheckName.DocumentImageInconsistentTimestampDetection:
    case CheckName.DocumentPdfInconsistentTimestampDetection:
      return computeDocumentPdfInconsistentTimestampDetectionMetadata(check);
    case CheckName.DocumentPdfAbnormalFontDetection:
      return computeDocumentPdfAbnormalFontDetectionMetadata(check);
    case CheckName.DocumentPdfAnnotationDetection:
      return computeDocumentPdfAnnotationDetectionMetadata(check);
    case CheckName.DocumentPdfContentTypeDetection:
      return computeDocumentPdfContentTypeDetectionMetadata(check);
    // Note: DocumentPdfModificationDetection uses the exact same logic as DocumentPdfEditorDetection,
    // since it contains the same metadata attributes (DocumentPdfModificationDetection is deprecated
    // in favor of DocumentPdfEditorDetection).
    case CheckName.DocumentPdfEditorDetection:
    case CheckName.DocumentPdfModificationDetection:
      return computeDocumentPdfEditorDetectionMetadata(check);
    case CheckName.DocumentRecencyDetection:
      return computeDocumentRecencyDetectionMetadata(check);
    case CheckName.DocumentSyntheticContentDetection:
      return computeDocumentSyntheticContentDetectionMetadata(
        check as DocumentSyntheticContentDetection,
      );
    case CheckName.IdAamvaDatabaseLookup:
      return computeIdAamvaDatabaseLookupMetadata(check as IdAamvaDatabaseLookup);
    case CheckName.IdAccountComparison:
      return computeIdAccountComparisonMetadata(check as IdAccountComparison);
    case CheckName.IdAgeComparison:
      return computeIdAgeComparisonMetadata(check);
    case CheckName.IdAgeInconsistencyDetection:
      return computeIdAgeInconsistencyDetectionMetadata(check as IdAgeInconsistencyDetection);
    case CheckName.IdAttributeComparison:
      return computeIdAttributeComparisonMetadata(check as IdAttributeComparison);
    case CheckName.IdBarcodeDetection:
      return computeIdBarcodeDetectionMetadata(check, hasPermissionVerificationGovIdNumberView);
    case CheckName.IdBarcodeInconsistencyDetection:
      return computeIdBarcodeInconsistencyDetectionMetadata(
        check as IdBarcodeInconsistencyDetection,
      );
    case CheckName.IdBlurDetection:
      return computeIdBlurDetectionMetadata(frontPhotoUrl, backPhotoUrl);
    case CheckName.IdColorInconsistencyDetection:
      return computeIdColorInconsistencyDetectionMetadata(check as IdColorInconsistencyDetection);
    case CheckName.IdCompromisedDetection:
      return computeIdCompromisedDetectionMetadata(
        check as IdCompromisedDetection,
        frontPhotoUrl,
        backPhotoUrl,
      );
    case CheckName.IdDisallowedCountryDetection:
      return computeIdDisallowedCountryDetectionMetadata(check);
    case CheckName.IdDisallowedTypeDetection:
      return computeIdDisallowedTypeDetectionMetadata(check);
    case CheckName.IdDoubleSideDetection:
      return computeIdDoubleSideDetectionMetadata(frontPhotoUrl, backPhotoUrl);
    case CheckName.IdExtractedPropertiesDetection:
      return computeIdExtractedPropertiesDetectionMetadata(
        check as IdExtractedPropertiesDetection,
        hasPermissionVerificationGovIdNumberView,
      );
    case CheckName.IdExtractionInconsistencyDetection:
      return computeIdExtractionInconsistencyDetectionMetadata(
        check as IdExtractionInconsistencyDetection,
      );
    case CheckName.IdExpiredDetection:
      return computeIdExpiredDetectionMetadata(check as IdExpiredDetection);
    case CheckName.IdExperimentalModelDetection:
      return computeIdExperimentalModelDetectionMetadata(check as IdExperimentalModelDetection);
    case CheckName.IdFabricationDetection:
      return computeIdFabricationDetectionMetadata(check as IdFabricationDetection);
    case CheckName.IdInquiryComparison:
      return computeIdInquiryComparisonMetadata(check as IdInquiryComparison);
    case CheckName.IdMrzDetection:
      return computeIdMrzDetectionMetadata(check, hasPermissionVerificationGovIdNumberView);
    case CheckName.IdMrzInconsistencyDetection:
      return computeIdMrzInconsistencyDetectionMetadata(check);
    case CheckName.IdNfcAttributeComparison:
      return computeIdNfcAttributeComparisonMetadata(check as IdNfcAttributeComparison);
    case CheckName.IdNumberFormatInconsistencyDetection:
      return computeIdNumberFormatInconsistencyDetectionMetadata(
        check,
        hasPermissionVerificationGovIdNumberView,
      );
    case CheckName.IdPaperDetection:
      return computeIdPaperDetectionMetadata(check as IdPaperDetection);
    case CheckName.IdPortraitClarityDetection:
      return computeIdPortraitClarityDetectionMetadata(check as IdPortraitClarityDetection);
    case CheckName.IdInconsistentRepeatDetection:
      return computeIdInconsistentRepeatDetectionMetadata(
        check as IdInconsistentRepeatDetection,
        frontPhotoUrl,
        backPhotoUrl,
      );
    case CheckName.IdPublicFigureDetection:
      return computeIdPublicFigureDetectionMetadata(check, frontPhotoUrl);
    case CheckName.IdRepeatDetection:
      return computeIdRepeatDetectionMetadata(
        check as IdRepeatDetection,
        frontPhotoUrl,
        backPhotoUrl,
      );
    case CheckName.IdSelfieComparison:
      return computeIdSelfieComparisonMetadata(check as IdSelfieComparison);
    case CheckName.IdValidDatesDetection:
      return computeIdValidDatesDetectionMetadata(check as IdValidDatesDetection);
    case CheckName.IdTamperDetection:
      return computeIdTamperDetectionMetadata(check as IdTamperDetection);
    case CheckName.MobileDriversLicenseAgeComparison:
      return computeMobileDriversLicenseAgeComparisonMetadata(
        check as MobileDriversLicenseAgeComparison,
      );
    case CheckName.SelfieAccountComparison:
      return computeSelfieAccountComparisonMetadata(check as SelfieAccountComparison);
    case CheckName.SelfieAgeComparison:
      return computeSelfieAgeComparisonMetadata(check as SelfieAgeComparison);
    case CheckName.SelfieAgeThresholdComparison:
      return computeSelfieAgeThresholdComparisonMetadata(check as SelfieAgeThresholdComparison);
    case CheckName.SelfieAgeInconsistencyDetection:
      return computeSelfieAgeInconsistencyDetectionMetadata(
        check as SelfieAgeInconsistencyDetection,
      );
    case CheckName.SelfieAttributeComparison:
      return computeSelfieAttributeComparisonMetadata(check as SelfieAttributeComparison);
    case CheckName.SelfieExperimentalModelDetection:
      return computeSelfieExperimentalModelDetectionMetadata(
        check as SelfieExperimentalModelDetection,
      );
    case CheckName.SelfieIdComparison:
      return computeSelfieIdComparisonMetadata(check as SelfieIdComparison);
    case CheckName.SelfieLivenessDetection:
      return computeSelfieLivenessDetectionMetadata(check);
    case CheckName.SelfiePublicFigureDetection:
      return computeSelfiePublicFigureDetectionMetadata(check);
    case CheckName.SelfieRepeatDetection:
      return computeSelfieRepeatDetectionMetadata(check as SelfieRepeatDetection);
    case CheckName.SelfieSimilarBackgroundDetection:
      return computeSelfieSimilarBackgroundDetectionMetadata(
        check as SelfieSimilarBackgroundDetection,
      );

    // The checks below do not render any metadata, but we do want to show the reason(s) if they failed
    case CheckName.BusinessWebsiteSufficientContentDetection:
    case CheckName.DatabaseEcbsvRecordMatch:
    case CheckName.DocumentCompromisedDetection:
    case CheckName.DocumentHandwritingDetection:
    case CheckName.DocumentImageCopyMoveDetection:
    case CheckName.DocumentImageSuspiciousMetadataDetection:
    case CheckName.DocumentJpegOriginalImageDetection:
    case CheckName.DocumentPortraitDetection:
    case CheckName.DocumentSelfieComparison:
    case CheckName.DocumentTamperDetection:
    case CheckName.DocumentTypeDetection:
    case CheckName.DocumentUnprocessableSubmissionDetection:
    case CheckName.IdElectronicReplicaDetection:
    case CheckName.IdEntityDetection:
    case CheckName.IdGlareDetection:
    case CheckName.IdHandwritingDetection:
    case CheckName.IdNfcDisallowedCountryDetection:
    case CheckName.IdNfcExpiredDetection:
    case CheckName.IdNfcTamperDetection:
    case CheckName.IdNfcUnprocessableSubmissionDetection:
    case CheckName.IdPortraitDetection:
    case CheckName.IdRealIdDetection:
    case CheckName.IdUnprocessableSubmissionDetection:
    case CheckName.IdVideoQualityDetection:
    case CheckName.SelfiePortraitQualityDetection:
    case CheckName.SelfieVideoQualityDetection:
      return {
        metadata: [],
        hideIfVerificationPassed: false,
        showReasonsIfVerificationFailed: true,
      };

    // The checks below do not render any metadata, but we do want to show the reason(s) if they are failed or are N/A
    case CheckName.DatabaseEcbsvDeathRecord:
      return {
        metadata: [],
        hideIfVerificationPassed: false,
        showReasonsIfVerificationFailed: true,
        showReasonsIfVerificationNotApplicable: true,
      };
    default:
      return {
        metadata: [],
        hideIfVerificationPassed: false,
      };
  }
};

export const computeMultipleChecksMetadata = (verification: Verification): CheckMetadata => {
  switch (verification.type) {
    case ApiModel.VerificationAamva:
      return computeAamvaAttributeChecksMetadata(verification as VerificationAamva);
    case ApiModel.VerificationBusinessWebsite:
      return computeBusinessWebsiteAttributeChecksMetadata(
        verification as VerificationBusinessWebsite,
      );
    case ApiModel.VerificationDatabase:
      return computeDatabaseAttributeChecksMetadata(verification as VerificationDatabase);
    case ApiModel.VerificationDatabaseAadhaar:
      return computeDatabaseAadhaarAttributeChecksMetadata(
        verification as VerificationDatabaseAadhaar,
      );
    case ApiModel.VerificationDatabaseBusiness:
      return computeDatabaseBusinessAttributeChecksMetadata(
        verification as VerificationDatabaseBusiness,
      );
    case ApiModel.VerificationDatabaseBusinessFootprint:
      return computeDatabaseBusinessFootprintAttributeChecksMetadata(
        verification as VerificationDatabaseBusinessFootprint,
      );
    case ApiModel.VerificationDatabaseStandard:
      return computeDatabaseStandardAttributeChecksMetadata(
        verification as VerificationDatabaseStandard,
      );
    case ApiModel.VerificationDatabasePhoneCarrier:
      return computeDatabasePhoneCarrierAttributeChecksMetadata(
        verification as VerificationDatabasePhoneCarrier,
      );
    case ApiModel.VerificationDatabaseVatNumber:
      return computeDatabaseVatNumberAttributeChecksMetadata(
        verification as VerificationDatabaseVatNumber,
      );
    default:
      return {
        metadata: [],
      };
  }
};
